/**
 * EdgeFilterConfig Component
 *
 * Custom configuration component for edge filters
 * Allows selecting:
 * - Edge type (ragam, composer, etc.)
 * - Filter mode (has any, has specific, has count)
 * - Specific target nodes (using EdgeSelector-style picker)
 * - Count comparisons (for "has more than X" filters)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { selectCatalog } from '../../Features/BhajanSlice';
import { NodeTypeId, NodeTypeNames, EdgeCardinality } from '../../types/graph.types';
import { NodeTypeDefinitions, getEdgeDefinition } from '../../config/nodeTypes';
import { createNodesSearchContext, searchNodes, NodesSearchContext, SearchableNode } from '../../utils/search';
import {
  FilterConfigProps,
  FilterOperator,
  EdgeFilterValue,
  CountOperator,
} from '../types';

/**
 * Edge labels for display
 */
const edgeLabels: Record<string, string> = {
  ragam: 'Ragam',
  talam: 'Talam',
  composer: 'Composer',
  deity: 'Deity',
  lyrics: 'Lyrics',
  audible: 'Audible',
  meaning: 'Meaning',
  compositionLanguage: 'Language',
  bb: 'Bhajan Babies',
  pitch: 'Pitch',
  album: 'Album',
  track: 'Track',
  collection: 'Collection',
  bhajan: 'Bhajan',
  rashi: 'Rashi',
  star: 'Star',
  bodyPart: 'Body Part',
  chakra: 'Chakra',
  rashiElem: 'Rashi Element',
  rashiGem: 'Rashi Gem',
  rashiColor: 'Rashi Color',
  rashiTree: 'Rashi Tree',
  chakraElem: 'Chakra Element',
  chakraGem: 'Chakra Gem',
  chakraColor: 'Chakra Color',
  element: 'Element',
  gem: 'Gem',
  color: 'Color',
  tree: 'Tree',
  place: 'Place',
  concert: 'Concert',
  concertTrack: 'Concert Track',
  thithi: 'Thithi',
};

function getEdgeLabel(edgeName: string): string {
  return edgeLabels[edgeName] || edgeName.charAt(0).toUpperCase() + edgeName.slice(1);
}

/**
 * Mode selector for edge filter
 */
type EdgeFilterMode = 'hasAny' | 'containsOneOf' | 'containsAllOf' | 'hasCount';

interface EdgeFilterConfigProps extends FilterConfigProps {}

export const EdgeFilterConfig: React.FC<EdgeFilterConfigProps> = ({
  filter,
  definition,
  onUpdate,
  onClose,
  nodes: filterNodes = [],
}) => {
  // Pull all nodes from our app's Redux (BhajanSlice). Admin used a separate
  // node-store slice with two derived shapes the component expects:
  //   nodes        — Record<nodeId, SearchableNode>
  //   nodesByType  — Record<nodeTypeId, number[]>
  // We compute both from our flat node list once.
  const catalog = useSelector(selectCatalog);
  const allNodes = useMemo<SearchableNode[]>(
    () => (catalog?.nodes ?? []),
    [catalog]
  );
  const nodes = useMemo<Record<number, SearchableNode>>(() => {
    const out: Record<number, SearchableNode> = {};
    for (const n of allNodes) out[n.nodeId] = n;
    return out;
  }, [allNodes]);
  const nodesByType = useMemo<Record<number, number[]>>(() => {
    const out: Record<number, number[]> = {};
    for (const n of allNodes) {
      const t = n.nodeTypeId;
      if (t == null) continue;
      (out[t] ||= []).push(n.nodeId);
    }
    return out;
  }, [allNodes]);
  const isLoading = false;
  // No-op shim — the bhajan saga loads all non-lyrics nodes on boot,
  // so the lazy-fetch dispatches admin used aren't needed here.
  const dispatch = (_action: unknown) => undefined;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetchNodesRequest = (_payload: unknown) => ({ type: 'noop' });

  // Parse current filter value
  const currentValue = useMemo<EdgeFilterValue>(() => {
    const val = filter.value;
    if (typeof val === 'object' && val !== null && 'edgeName' in val) {
      return val as EdgeFilterValue;
    }
    // Legacy: plain string edge name
    return { edgeName: String(val) };
  }, [filter.value]);

  // Detect node type from the passed nodes prop (these are the nodes being filtered)
  const detectedNodeTypeId = useMemo<NodeTypeId | undefined>(() => {
    if (filterNodes.length === 0) return undefined;
    const firstType = filterNodes[0]?.nodeTypeId;
    const allSameType = filterNodes.every(n => n?.nodeTypeId === firstType);
    return allSameType ? firstType : undefined;
  }, [filterNodes]);

  // Get available edges for the detected node type
  const availableEdges = useMemo(() => {
    if (!detectedNodeTypeId) return [];
    const nodeTypeDef = NodeTypeDefinitions[detectedNodeTypeId];
    if (!nodeTypeDef) return [];
    return nodeTypeDef.edges.map(e => ({
      name: e.name,
      label: getEdgeLabel(e.name),
      targetNodeTypeId: e.targetNodeTypeId,
      cardinality: e.cardinality,
    }));
  }, [detectedNodeTypeId]);

  // State - edgeName must be declared before useMemo that uses it
  const [edgeName, setEdgeName] = useState(currentValue.edgeName || availableEdges[0]?.name || '');

  // Get the selected edge's cardinality
  const selectedEdgeCardinality = useMemo(() => {
    const edge = availableEdges.find(e => e.name === edgeName);
    return edge?.cardinality || EdgeCardinality.Single;
  }, [availableEdges, edgeName]);

  const isMultiEdge = selectedEdgeCardinality === EdgeCardinality.Multi;

  const [mode, setMode] = useState<EdgeFilterMode>(() => {
    if (currentValue.targetNodeIds && currentValue.targetNodeIds.length > 0) {
      // Check if it's containsAllOf based on matchAll flag
      return currentValue.matchAll ? 'containsAllOf' : 'containsOneOf';
    }
    if (currentValue.countComparison) return 'hasCount';
    // Default to containsOneOf (Has specific) as it's the most common use case
    return 'containsOneOf';
  });
  const [selectedNodeIds, setSelectedNodeIds] = useState<number[]>(currentValue.targetNodeIds || []);
  const [countOperator, setCountOperator] = useState<CountOperator>(
    currentValue.countComparison?.operator || 'greaterThanOrEqual'
  );
  const [countValue, setCountValue] = useState<number>(currentValue.countComparison?.value || 1);

  // Node picker state
  const [showNodePicker, setShowNodePicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef(false);

  // Get target node type for selected edge
  const targetNodeTypeId = useMemo(() => {
    if (!detectedNodeTypeId || !edgeName) return undefined;
    const edgeDef = getEdgeDefinition(detectedNodeTypeId, edgeName);
    return edgeDef?.targetNodeTypeId;
  }, [detectedNodeTypeId, edgeName]);

  const targetNodeIds = targetNodeTypeId ? (nodesByType[targetNodeTypeId] || []) : [];
  const targetNodes = useMemo(() =>
    targetNodeIds.map(id => nodes[id]).filter(Boolean),
    [targetNodeIds, nodes]
  );

  // Fetch target nodes when needed
  useEffect(() => {
    if ((mode === 'containsOneOf' || mode === 'containsAllOf') && targetNodeTypeId && !hasFetchedRef.current && targetNodeIds.length === 0 && !isLoading) {
      hasFetchedRef.current = true;
      dispatch(fetchNodesRequest({
        nodeTypeId: targetNodeTypeId,
        pageSize: 500
      }));
    }
  }, [dispatch, targetNodeTypeId, targetNodeIds.length, isLoading, mode]);

  // Reset hasFetchedRef when target type changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [targetNodeTypeId]);

  // Also fetch when node picker opens
  useEffect(() => {
    if (showNodePicker && targetNodeTypeId && targetNodeIds.length === 0 && !isLoading) {
      dispatch(fetchNodesRequest({
        nodeTypeId: targetNodeTypeId,
        pageSize: 500
      }));
    }
  }, [showNodePicker, dispatch, targetNodeTypeId, targetNodeIds.length, isLoading]);

  const getNodeDisplayName = useCallback((node: SearchableNode): string => {
    // SearchableNode.data has an index signature `[key: string]: unknown`, so
    // the four display-name fields below resolve to `unknown` without this
    // cast. At runtime they are always strings (or absent); the cast is
    // types-only and preserves the original `||` fall-through behavior.
    const data = node.data as {
      teName?: string;
      enName?: string;
      name?: string;
      teNormalizedName?: string;
      enNormalizedName?: string;
    } | undefined;
    const name = data?.teName ||
                 data?.enName ||
                 data?.name ||
                 data?.teNormalizedName ||
                 data?.enNormalizedName;
    if (name) return name;
    const typeName = NodeTypeNames[node.nodeTypeId as NodeTypeId] || 'Node';
    return `${typeName} #${node.nodeId}`;
  }, []);

  // Create search context
  const searchContext = useMemo<NodesSearchContext<SearchableNode> | null>(() => {
    if (targetNodes.length === 0) return null;
    return createNodesSearchContext(targetNodes);
  }, [targetNodes]);

  // Sort and filter nodes
  const sortedTargetNodes = useMemo(() => {
    return [...targetNodes].sort((a, b) => {
      const nameA = getNodeDisplayName(a).toLowerCase();
      const nameB = getNodeDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [targetNodes, getNodeDisplayName]);

  const filteredNodes = useMemo(() => {
    if (!searchQuery) return sortedTargetNodes;
    if (!targetNodeTypeId) return sortedTargetNodes;
    const result = searchNodes(searchQuery, searchContext, targetNodeTypeId);
    return result.nodes.sort((a, b) => {
      const nameA = getNodeDisplayName(a).toLowerCase();
      const nameB = getNodeDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [sortedTargetNodes, searchQuery, searchContext, targetNodeTypeId, getNodeDisplayName]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showNodePicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNodePicker(false);
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNodePicker]);

  const handleSelectNode = (nodeId: number) => {
    if (selectedNodeIds.includes(nodeId)) {
      setSelectedNodeIds(selectedNodeIds.filter(id => id !== nodeId));
    } else {
      setSelectedNodeIds([...selectedNodeIds, nodeId]);
    }
  };

  const handleRemoveNode = (nodeId: number) => {
    setSelectedNodeIds(selectedNodeIds.filter(id => id !== nodeId));
  };

  const handleApply = () => {
    let operator: FilterOperator;
    let value: EdgeFilterValue;

    switch (mode) {
      case 'containsOneOf':
        operator = FilterOperator.HasSpecific;
        value = {
          edgeName,
          targetNodeIds: selectedNodeIds,
          matchAll: false,
        };
        break;
      case 'containsAllOf':
        operator = FilterOperator.HasSpecific;
        value = {
          edgeName,
          targetNodeIds: selectedNodeIds,
          matchAll: true,
        };
        break;
      case 'hasCount':
        operator = FilterOperator.HasCount;
        value = {
          edgeName,
          countComparison: {
            operator: countOperator,
            value: countValue,
          },
        };
        break;
      case 'hasAny':
      default:
        operator = filter.operator === FilterOperator.NotHas ? FilterOperator.NotHas : FilterOperator.Has;
        value = { edgeName };
        break;
    }

    onUpdate(operator, value);
  };

  const selectedNodes = useMemo(() => {
    return selectedNodeIds.map(id => nodes[id]).filter(Boolean);
  }, [selectedNodeIds, nodes]);

  // Count operator labels
  const countOperatorLabels: Record<string, string> = {
    equals: 'equals',
    greaterThan: 'more than',
    lessThan: 'less than',
    greaterThanOrEqual: 'at least',
    lessThanOrEqual: 'at most',
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-light pb-3">
        <h3 className="text-lg font-display font-medium text-text-primary">
          Configure Relationship Filter
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full text-text-tertiary hover:text-text-secondary hover:bg-bg-3/60 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Mode selector */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Filter Mode
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setMode('containsOneOf')}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              mode === 'containsOneOf'
                ? 'bg-accent-indigo text-white border-accent-indigo'
                : 'bg-bg-2 text-text-secondary border-light hover:bg-bg-3/60'
            }`}
          >
            Has specific
          </button>
          {isMultiEdge && (
            <button
              type="button"
              onClick={() => setMode('containsAllOf')}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                mode === 'containsAllOf'
                  ? 'bg-accent-indigo text-white border-accent-indigo'
                  : 'bg-bg-2 text-text-secondary border-light hover:bg-bg-3/60'
              }`}
            >
              Contains all of
            </button>
          )}
          <button
            type="button"
            onClick={() => setMode('hasAny')}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              mode === 'hasAny'
                ? 'bg-accent-indigo text-white border-accent-indigo'
                : 'bg-bg-2 text-text-secondary border-light hover:bg-bg-3/60'
            }`}
          >
            Has any connection
          </button>
          {isMultiEdge && (
            <button
              type="button"
              onClick={() => setMode('hasCount')}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                mode === 'hasCount'
                  ? 'bg-accent-indigo text-white border-accent-indigo'
                  : 'bg-bg-2 text-text-secondary border-light hover:bg-bg-3/60'
              }`}
            >
              Has count
            </button>
          )}
        </div>
      </div>

      {/* Edge selector */}
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1.5">
          Relationship Type
        </label>
        <select
          value={edgeName}
          onChange={(e) => {
            const newEdgeName = e.target.value;
            setEdgeName(newEdgeName);
            setSelectedNodeIds([]); // Clear selected nodes when edge changes

            // Reset mode if switching to single-cardinality edge and current mode is multi-only
            const newEdge = availableEdges.find(edge => edge.name === newEdgeName);
            const newIsMulti = newEdge?.cardinality === EdgeCardinality.Multi;
            if (!newIsMulti && (mode === 'containsAllOf' || mode === 'hasCount')) {
              setMode('hasAny');
            }
          }}
          className="w-full px-3 py-2 border border-light rounded-md bg-bg-2 text-text-primary focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40 text-sm"
        >
          {availableEdges.map((edge) => (
            <option key={edge.name} value={edge.name}>
              {edge.label}
            </option>
          ))}
        </select>
      </div>

      {/* Mode-specific configuration */}
      {(mode === 'containsOneOf' || mode === 'containsAllOf') && (
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Select {getEdgeLabel(edgeName)}(s)
          </label>

          {/* Selected nodes */}
          {selectedNodes.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedNodes.map(node => (
                <div
                  key={node.nodeId}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-accent-indigo/15 border border-accent-indigo/30 text-text-primary rounded-full text-sm"
                >
                  <span>{getNodeDisplayName(node)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveNode(node.nodeId)}
                    className="text-text-secondary hover:text-text-primary"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Node picker */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowNodePicker(!showNodePicker)}
              className="w-full px-3 py-2 text-left border border-light rounded-md bg-bg-2 text-text-secondary flex items-center justify-between hover:bg-bg-3/60 transition-colors"
            >
              <span>
                {selectedNodes.length === 0
                  ? `Select ${getEdgeLabel(edgeName).toLowerCase()}...`
                  : `${selectedNodes.length} selected`
                }
              </span>
              <PlusIcon className="h-5 w-5 text-text-tertiary" />
            </button>

            {showNodePicker && (
              <div className="absolute z-20 mt-1 w-full bg-bg-1 border border-light rounded-xl shadow-2xl">
                {/* Search */}
                <div className="p-2 border-b border-light">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-tertiary" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-light bg-bg-2 text-text-primary placeholder-text-tertiary rounded focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40 outline-none"
                      placeholder="Search..."
                      autoFocus
                    />
                  </div>
                </div>

                {/* Loading */}
                {isLoading && targetNodes.length === 0 && (
                  <div className="px-4 py-3 text-sm text-text-tertiary text-center">
                    Loading...
                  </div>
                )}

                {/* Options */}
                <div className="max-h-64 overflow-y-auto">
                  {!isLoading && filteredNodes.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-text-tertiary">
                      {targetNodes.length === 0 ? 'No items available' : 'No items found'}
                    </div>
                  ) : (
                    filteredNodes.map(node => {
                      const isSelected = selectedNodeIds.includes(node.nodeId);
                      return (
                        <button
                          key={node.nodeId}
                          type="button"
                          onClick={() => handleSelectNode(node.nodeId)}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            isSelected
                              ? 'bg-accent-indigo/15 text-text-primary'
                              : 'text-text-secondary hover:bg-bg-3/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{getNodeDisplayName(node)}</span>
                            {isSelected && <CheckIcon className="h-4 w-4 text-accent-gold" />}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Close */}
                <div className="p-2 border-t border-light">
                  <button
                    type="button"
                    onClick={() => setShowNodePicker(false)}
                    className="w-full text-sm text-text-secondary hover:text-text-primary"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'hasCount' && (
        <div className="flex gap-3 items-center">
          <select
            value={countOperator}
            onChange={(e) => setCountOperator(e.target.value as CountOperator)}
            className="px-3 py-2 border border-light rounded-md bg-bg-2 text-text-primary focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40 text-sm"
          >
            {Object.entries(countOperatorLabels).map(([op, label]) => (
              <option key={op} value={op}>{label}</option>
            ))}
          </select>
          <input
            type="number"
            min="0"
            value={countValue}
            onChange={(e) => setCountValue(parseInt(e.target.value, 10) || 0)}
            className="w-20 px-3 py-2 border border-light rounded-md bg-bg-2 text-text-primary focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40 text-sm"
          />
          <span className="text-sm text-text-secondary">
            {getEdgeLabel(edgeName)}(s)
          </span>
        </div>
      )}

      {/* Preview */}
      <div className="pt-2 border-t border-light">
        <p className="text-sm text-text-tertiary">
          Preview:{' '}
          <span className="font-medium text-text-secondary">
            {mode === 'hasAny' && `Has any ${getEdgeLabel(edgeName)} connection`}
            {mode === 'containsOneOf' && selectedNodes.length > 0 && (
              `${getEdgeLabel(edgeName)} contains one of: ${selectedNodes.map(n => getNodeDisplayName(n)).join(', ')}`
            )}
            {mode === 'containsOneOf' && selectedNodes.length === 0 && (
              `Select at least one ${getEdgeLabel(edgeName).toLowerCase()}`
            )}
            {mode === 'containsAllOf' && selectedNodes.length > 0 && (
              `${getEdgeLabel(edgeName)} contains all of: ${selectedNodes.map(n => getNodeDisplayName(n)).join(', ')}`
            )}
            {mode === 'containsAllOf' && selectedNodes.length === 0 && (
              `Select at least one ${getEdgeLabel(edgeName).toLowerCase()}`
            )}
            {mode === 'hasCount' && (
              `Has ${countOperatorLabels[countOperator]} ${countValue} ${getEdgeLabel(edgeName)}(s)`
            )}
          </span>
        </p>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-3 border-t border-light">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-2 border border-light rounded-md hover:bg-bg-3/60 focus:outline-none focus:ring-2 focus:ring-accent-gold/40 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={(mode === 'containsOneOf' || mode === 'containsAllOf') && selectedNodeIds.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-accent-indigo hover:bg-accent-indigo/90 disabled:bg-accent-indigo/40 disabled:cursor-not-allowed rounded-md focus:outline-none focus:ring-2 focus:ring-accent-gold/40 focus:ring-offset-2 focus:ring-offset-bg-1 transition-colors"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default EdgeFilterConfig;
